import { Field, HelperMessage, ValidMessage } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import memoize from 'lodash/memoize';
import React, { FC, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AsyncSelect } from '@atlaskit/select';
import { OrgDTO, OrgDTOFromJSON } from '@atlassiansox/phobos-client-ts';

import { MAX_TEXT_LENGTH } from '@/constants';
import { useAutoCompleteController, useFetchErrorController } from '@/controllers';
import { useBetterDebounce } from '@/controllers/search-autocomplete-controller';
import { TeamFormData, api2Ui } from '@/components/composed/forms/data-mapper';
import { apis, apiResolve, apiFetch } from '@/services';
import { FormFieldTooltipLabel, formatOptionLabel } from '@/components/primitive';
import { querySpec } from '@/services/api-service/api-queries';
import { ConditionalErrorMessage } from '@/components/composed/forms/form-errors';

import { FilterOptionOption } from '../team-org-model/types';

import { helperCopy } from './copy';

type TeamFormProps = {
  isLoading: boolean;
} & Partial<TeamFormData>;

const TeamForm: FC<TeamFormProps> = ({
  isLoading,
  name = '',
  description = '',
  opsgenieTeam = '',
  atlassianOrgId = '',
}) => {
  const searchName = useAutoCompleteController('team');
  const searchOrg = useAutoCompleteController('org', { optionValuekey: 'orgId' });
  const validateName = React.useCallback(
    memoize((searchString?: string) => {
      if (!searchString) {
        return 'Name must be provided';
      }

      // user is allowed to use the predefined name
      if (searchString === name) {
        return undefined;
      }

      return searchName(searchString).then(d => {
        if (
          d.find(({ name: entityName }) => entityName?.toLowerCase() === searchString.toLowerCase())
        ) {
          return 'Name already in use';
        }

        return undefined;
      });
    }),
    [name, searchName]
  );
  const validateOrg = useCallback((searchString?: string) => {
    if (!searchString) {
      return 'Atlassian Org Team must be selected';
    }

    return undefined;
  }, []);

  const searchOpsgenieTeam = useBetterDebounce<string, Error | undefined>(
    (teamName, callback) => {
      const request = apiResolve(
        apis.opsgenieTeam.getByName.bind(apis.opsgenieTeam, { name: teamName })()
      );
      return request.then(
        _ => callback(undefined),
        err => callback(err)
      );
    },
    undefined,
    []
  );
  const validateOpsgenieTeam = React.useCallback(
    (searchString?: string) => {
      if (!searchString) {
        return undefined;
      }

      // user is allowed to use the predefined name
      if (searchString === opsgenieTeam) {
        return undefined;
      }

      return searchOpsgenieTeam(searchString).then(err => {
        if (err) {
          return `Opsgenie team with name "${searchString}" not found`;
        }
        return undefined;
      });
    },
    [opsgenieTeam, searchOpsgenieTeam]
  );

  const hasOrg: boolean = !!atlassianOrgId;
  const { onError } = useFetchErrorController();
  const { queryKey, path } = querySpec('org.{id}', { id: atlassianOrgId });
  const { data: rawOrgData } = useQuery(queryKey, () => apiFetch(path), {
    onError,
    enabled: !!atlassianOrgId,
  });
  const dataOrg: OrgDTO = useMemo(() => OrgDTOFromJSON(rawOrgData || {}), [rawOrgData]);
  const defaultOrgValue = hasOrg ? api2Ui.org(dataOrg) : null;

  return (
    <>
      <Field<string>
        name="name"
        label={<FormFieldTooltipLabel isRequired label="Name" content={helperCopy.team.name} />}
        validate={validateName}
        isRequired
        isDisabled={isLoading}
        defaultValue={name}
      >
        {({ fieldProps, error, valid, meta }) => (
          <>
            <TextField
              maxLength={MAX_TEXT_LENGTH}
              placeholder="Name of the team, e.g. Delivery Engineering"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...fieldProps}
            />
            <ConditionalErrorMessage error={error} />
            {meta.validating && meta.dirty ? (
              <HelperMessage>Checking that team name is available...</HelperMessage>
            ) : null}
            {!meta.validating && valid && meta.dirty ? (
              <ValidMessage>Name is available</ValidMessage>
            ) : null}
          </>
        )}
      </Field>
      <Field<any>
        name="org"
        label={
          <FormFieldTooltipLabel
            isRequired
            label="Atlassian Org Team"
            content={helperCopy.team.atlassianOrgTeam}
          />
        }
        validate={validateOrg}
        isRequired
        isDisabled={isLoading}
        defaultValue={defaultOrgValue}
      >
        {({ fieldProps: { id, ...rest }, error }) => (
          <>
            <AsyncSelect
              formatOptionLabel={formatOptionLabel}
              placeholder="An Atlassian Org team, e.g. Eng - Engineering Maturity - Tome"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              inputId={id}
              defaultOptions
              loadOptions={searchOrg}
              filterOption={(option: FilterOptionOption<OrgDTO>) => option.data.level === 'team'}
              validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<string, HTMLTextAreaElement>
        name="description"
        label="Description"
        defaultValue={description}
        isDisabled={isLoading}
      >
        {({ fieldProps, error }) => (
          <>
            {/* @ts-ignore */}
            <TextArea minimumRows={3} {...fieldProps} maxLength={MAX_TEXT_LENGTH} />
            <ConditionalErrorMessage error={error} />
          </>
        )}
      </Field>
      <Field<string>
        name="opsgenieTeam"
        label={
          <FormFieldTooltipLabel label="Opsgenie Team" content={helperCopy.team.opsgenieTeam} />
        }
        validate={validateOpsgenieTeam}
        isDisabled={isLoading}
        defaultValue={opsgenieTeam}
      >
        {({ fieldProps, error, valid, meta }) => (
          <>
            <TextField
              maxLength={MAX_TEXT_LENGTH}
              placeholder="Name of the Opsgenie team (case sensitive)"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...fieldProps}
            />
            <ConditionalErrorMessage error={error} />
            {meta.validating && meta.dirty ? (
              <HelperMessage>Checking that Opsgenie team exists...</HelperMessage>
            ) : null}
            {!meta.validating && valid && meta.dirty ? (
              <ValidMessage>Valid team</ValidMessage>
            ) : null}
          </>
        )}
      </Field>
    </>
  );
};

export default TeamForm;
